<template>
  <div class="container-parent">
    <InnerWebOffice
      v-if="viewResolvedUrl"
      :getTokenFun="getTokenFun"
      :refreshTokenFun="refreshTokenFun"
      ref="innerWebOffice"
      @ready="onOfficeReady"
    />
  </div>
</template>

<script setup>
/*
 * 阿里云在线文档组件。
 *
 * 在线预览、在线编辑 --- 注意此组件只支持同时打开一个，如果需同时打开多个，需调用同级的AliIframeOffice.vue组件。
 */
import InnerWebOffice from './inner/InnerWebOffice.vue'
import {getAliToken, getAliUrl, refreshAliToken} from "@/api/system/aLiOffice.js";
import useUserStore from '@/store/modules/user';

const props = defineProps({
  editConfig: { // 在线编辑时传参对象(目前仅支持学员E表做题)，和 viewConfig 二选一传
    type: Object,
    default: null
  },
  viewConfig: { // 在线预览时传参对象，和 editConfig 二选一传
    type: Object,
    default: null
  },
  autoFull: {// ppt自动开始放映
    type: Boolean,
    default: false,
  },
  watermark: { // 水印配置（后端API方式，适用于所有文档类型）
    type: Object,
    default: () => ({
      type: 1,
      value: `${useUserStore().userInfo.stuName}${useUserStore().userInfo.id}`,
      vertical: 150,
      horizontal: 150,
      font: 'normal 12px 微软雅黑',
      fillStyle: 'rgba(0, 0, 0, 0.1)'
    })
  },
})

const viewResolvedUrl = ref('') // 预览的文件地址是否已经解析了(仅针对预览)

onMounted(async() => {
  if (props.editConfig == null && props.viewConfig == null) {
    throw new Error("阿里云文档初始化失败：参数未传")
  }
  if (props.editConfig != null && props.viewConfig != null) {
    throw new Error("阿里云文档初始化失败：无法确定时预览还是编辑，两个参数不应该都传")
  }
  if (props.viewConfig != null && props.editConfig == null) { // 是预览模式
    if (props.viewConfig.fileUrl.indexOf("aliyuncs.com") > -1) { // 本身就是阿里云文件oss地址
      console.log('采用模式 [阿里云本身] 解析')
      viewResolvedUrl.value = props.viewConfig.fileUrl
    } else { // 本身不是，需要解析storageId
      let storageId

      const sampleText1 = 'storage/api/files/download/'
      const sampleText2 = '/system/study/center/toolkit/preview/'
      if (props.viewConfig.fileUrl.indexOf(sampleText1) > -1) {
        console.log('采用模式 [sampleText1] 解析')
        //这种格式：/storage/api/files/download/2001893574708637696?name=2001893574708637696.xls
        storageId = props.viewConfig.fileUrl.substring(props.viewConfig.fileUrl.indexOf(sampleText1) + sampleText1.length)
        storageId = storageId.substring(0, storageId.indexOf("?"))
      } else if (props.viewConfig.fileUrl.indexOf(sampleText2) > -1) {
        console.log('采用模式 [sampleText2] 解析')
        //这种格式：/system/study/center/toolkit/preview/1929761366263259136/大文件.xls
        storageId = props.viewConfig.fileUrl.substring(props.viewConfig.fileUrl.indexOf(sampleText2) + sampleText2.length)
        storageId = storageId.substring(0, storageId.indexOf("/"))
      }

      if (storageId == null) {
        console.error('storageId解析失败，信息：', {fileUrl: props.viewConfig.fileUrl})
        throw new Error("storageId解析失败")
      }

      const res = await getAliUrl([storageId])
      viewResolvedUrl.value = res.data && res.data[0]?.storageUrl || ''
    }

    console.log('预览模式，最终的viewResolvedUrl', viewResolvedUrl.value)
  }
  if (props.viewConfig == null && props.editConfig != null) { // 是在线编辑模式
    viewResolvedUrl.value = 'edit' // 编辑模式，直接忽略，执行初始化
  }
})

const accessToken = ref('')
const refreshToken = ref('')

// 定义获取 token函数，调用 web 服务端接口，服务端调用 IMM GenerateWebofficeToken 接口
async function getTokenFun() {
  const reqData = {}
  if (props.editConfig != null) { // 编辑(目前只有学员做题)
    switch (props.editConfig.scene) {
      case 'record': // 学员做题
        reqData.scene = 'record'
        reqData.examStudentInfoId = props.editConfig.examStudentInfoId
        reqData.queId = props.editConfig.queId
        break
    }
  } else if (props.viewConfig != null) { // 预览
    reqData.scene = 'preview'
    reqData.fileUrl = viewResolvedUrl.value
    reqData.watermark = props.watermark || {}
  }
  const res = await getAliToken(reqData)
  accessToken.value = res.data.accessToken
  refreshToken.value = res.data.refreshToken
  return {
    RefreshToken: res.data.refreshToken,
    RequestId: res.data.requestId,
    AccessToken: res.data.accessToken,
    RefreshTokenExpiredTime: res.data.refreshTokenExpiredTime,
    WebofficeURL: res.data.webofficeURL,
    AccessTokenExpiredTime: res.data.accessTokenExpiredTime,
  }
}

// 定义刷新 token 函数，调用 web 服务端接口，服务端调用 IMM RefreshWebofficeToken 接口
async function refreshTokenFun() {
  const res = await refreshAliToken({
    accessToken: accessToken.value,
    refreshToken: refreshToken.value,
    fileUrl: viewResolvedUrl.value,
  })
  accessToken.value = res.data.accessToken
  refreshToken.value = res.data.refreshToken
  return {
    RefreshToken: res.data.refreshToken,
    RequestId: res.data.requestId,
    AccessToken: res.data.accessToken,
    RefreshTokenExpiredTime: res.data.refreshTokenExpiredTime,
    WebofficeURL: res.data.webofficeURL,
    AccessTokenExpiredTime: res.data.accessTokenExpiredTime,
  }
}

const innerWebOffice = ref()

function onOfficeReady() {
  if (props.autoFull
    && Boolean(viewResolvedUrl.value
      && /\.(ppt|pptx|pps|ppsx|pot|potx)$/i.test(viewResolvedUrl.value))
  ) { // 是ppt格式并且需要autoFull
    innerWebOffice.value.pptFullPlay()
  }
}

// 主动保存阿里文档
function activeSave() {
  return innerWebOffice.value.activeSave()
}

defineExpose({
  activeSave,
})
</script>


<style lang="scss">
.container-parent {
  width: 100%;
  height: 100%;

  #office-iframe {
    width: 100% !important;
    height: 100% !important;
  }
}
</style>
